import {
  Button,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Portal
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import useWindowScroll from "@saleor/hooks/useWindowScroll";
import { buttonMessages } from "@saleor/intl";
import { OrderDetails_order } from "@saleor/motherOrders/types/OrderDetails";
import { isNationwideRTS } from "@saleor/motherOrders/utils/handlers";
import StoreOrderInStoreTooltip from "@saleor/storeOrders/components/StoreOrderInStoreTooltip";
import classNames from "classnames";
import React, { useCallback, useState } from "react";
import { useIntl } from "react-intl";

import { useAppAction } from "../AppLayout/AppActionContext";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "../ConfirmButton/ConfirmButton";
import Container from "../Container";

const useStyles = makeStyles(
  theme => ({
    activateButton: {
      "& span": {
        color: theme.palette.error.main
      },
      backgroundColor: "#ffffff",
      border: "1px solid #1848FE",
      marginRight: theme.spacing(2)
    },
    activateText: {
      color: "#1848FE"
    },
    alternateSaveButton: {
      "&:active": {
        "& span": {
          "& span": {
            color: "#002972"
          }
        },
        backgroundColor: "#a7b9fe",
        boxShadow: "inset 0px 0px 0px 2px #0228b8"
      },
      "&:disabled": {
        backgroundColor: "#1848FE",
        opacity: "0.4"
      },
      "&:hover": {
        backgroundColor: "#315cfe",
        boxShadow: "inset 0px 0px 0px 1px #0228b8"
      },
      backgroundColor: "#1848FE",
      marginRight: theme.spacing(2)
    },
    alternateSaveButtonLabel: {
      color: "#FFF"
    },
    button: {
      marginRight: theme.spacing(1)
    },
    cancelButton: {
      "& span": {
        color: theme.palette.primary.main
      },
      "&:disabled span": {
        color: "#BAC8FF"
      },
      backgroundColor: "#ffffff",
      border: "1px solid #E5E5E5",
      marginRight: theme.spacing(2)
    },
    cancelButtonOutlined: {
      border: "2px solid #9098A1",
      padding: "4px 14px"
    },
    cancelButtonSecondary: {
      "& span": {
        color: "#747C8C"
      },
      backgroundColor: "#ffffff",
      marginRight: theme.spacing(2)
    },
    content: {
      "&:last-child": {
        paddingBottom: theme.spacing(2)
      },
      display: "flex",
      paddingBottom: theme.spacing(2),
      paddingTop: theme.spacing(2),
      [theme.breakpoints.down("sm")]: {
        marginTop: theme.spacing(1)
      }
    },
    deactivateButton: {
      "& span": {
        color: theme.palette.error.main
      },
      backgroundColor: "#ffffff",
      border: "1px solid #EC5D77",
      marginRight: theme.spacing(2)
    },
    deleteButton: {
      "& span": {
        color: "#002972"
      },
      "&:disabled span": {
        color: "#BAC8FF"
      },
      "&:hover": {
        backgroundColor: theme.palette.error.dark
      },
      backgroundColor: theme.palette.error.main,
      color: theme.palette.error.contrastText
    },
    draftButton: {
      "&:active": {
        "& span": {
          "& span": {
            color: "#002972"
          }
        },
        backgroundColor: "#f7f8fc"
      },
      "&:disabled": {
        "& span": {
          "& span": {
            color: "#BAC8FF"
          }
        },
        backgroundColor: "#b8b8b8"
      },
      "&:hover": {
        backgroundColor: "#ffffff",
        boxShadow: "inset 0px 0px 0px 1px #0228b8"
      },
      backgroundColor: "#ffffff",
      marginRight: theme.spacing(2)
    },
    draftText: {
      color: "#1848FE"
    },
    editButton: {
      "&:disabled": {
        "& span": {
          "& span": {
            opacity: "0.4"
          }
        },
        opacity: "0.4"
      },
      "&:hover": {
        backgroundColor: "#F4F8FF",
        boxShadow: "inset 0px 0px 0px 1px #F4F8FF",
        color: "#0030FF"
      },
      backgroundColor: "#F4F8FF",
      boxShadow: "inset 0px 0px 0px 2px #F4F8FF",
      color: "#0030FF",
      marginLeft: theme.spacing(2)
    },
    paper: {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0
    },
    publishButton: {
      "&:active": {
        "& span": {
          "& span": {
            color: "#002972"
          }
        },
        backgroundColor: "#a7b9fe",
        boxShadow: "inset 0px 0px 0px 2px #0228b8"
      },
      "&:disabled": {
        "& span": {
          "& span": {
            color: "#BAC8FF"
          }
        },
        backgroundColor: "#A7B9FE"
      },
      "&:hover": {
        backgroundColor: "#315cfe",
        boxShadow: "inset 0px 0px 0px 1px #0228b8"
      },
      backgroundColor: "#1848FE",
      marginRight: theme.spacing(2)
    },
    publishText: {
      color: "#FFF"
    },
    purpleSaveButton: {
      "&:active": {
        "& span": {
          "& span": {
            color: "#002972"
          }
        },
        backgroundColor: "#FFCE22",
        boxShadow: "inset 0px 0px 0px 2px #D0A73E"
      },
      "&:disabled": {
        "& span": {
          "& span": {
            color: "#FFFFFF"
          }
        },
        backgroundColor: "#BDBDBD"
      },
      "&:hover": {
        backgroundColor: "#FFD15C",
        boxShadow: "inset 0px 0px 0px 1px #D0A73E"
      },
      backgroundColor: "#BDBDBD"
    },
    root: {
      height: 70
    },
    saveButton: {
      "&:active": {
        "& span": {
          "& span": {
            color: "#002972"
          }
        },
        backgroundColor: "#FFCE22",
        boxShadow: "inset 0px 0px 0px 2px #D0A73E"
      },
      "&:disabled": {
        "& span": {
          "& span": {
            color: "#BAC8FF"
          }
        },
        backgroundColor: "#FFEDBE"
      },
      "&:hover": {
        backgroundColor: "#FFD15C",
        boxShadow: "inset 0px 0px 0px 1px #D0A73E"
      },
      backgroundColor: "#FFD15C"
    },
    spacer: {
      flex: "1"
    }
  }),
  { name: "SaveButtonBar" }
);

interface SaveButtonBarProps {
  /** Identifiers */
  id?: string;
  status?: string;
  order?: OrderDetails_order;

  /** Required state */
  disabled: boolean;
  state: ConfirmButtonTransitionState;

  /** Visibility toggles */
  hideSave?: boolean;
  hideActivate?: boolean;
  hideDeactivate?: boolean;
  showCancelButton?: boolean;
  forceDocked?: boolean;

  /** Button disabling */
  disableDelete?: boolean;
  disablePublishBtn?: boolean;
  disableAlternateButton?: boolean;
  disableDraft?: boolean;
  disableValidate?: boolean;
  disabledCancelBtn?: boolean;
  disableEditBtn?: boolean;

  /** Button variants & styles */
  cancelButtonVariant?: boolean;
  cancelButtonOutlined?: boolean;
  purpleSaveButton?: boolean;
  saveButtonVariant?: boolean;

  /** Tooltip */
  showTooltip?: boolean;
  tooltipText?: string;

  /** Labels */
  labels?: {
    cancel?: string;
    delete?: string;
    draft?: string;
    save?: string;
    publish?: string;
    activate?: string;
    deactivate?: string;
    edit?: string;
  };

  /** Checkbox option */
  checkboxState?: {
    label: string;
    disabled: boolean;
    checked: boolean;
    styles?: any;
    onChange(event: any): any;
  };

  /** Event handlers */
  onCancel: () => void;
  onDelete?: () => void;
  onActivate?: () => void;
  onDeactivate?: () => void;
  onDraft?(event: any): void;
  onPublish?(event: any): void;
  onSave?(event: any): void;
  onEdit?(event: any): void;
}

export const SaveButtonBar: React.FC<SaveButtonBarProps> = props => {
  const {
    /** Identifiers */
    id,
    status,
    order,

    /** Required state */
    disabled,
    state,

    /** Visibility toggles */
    hideSave,
    hideActivate,
    hideDeactivate,
    showCancelButton,
    forceDocked,

    /** Button disabling */
    disableDelete,
    disablePublishBtn,
    disableAlternateButton,
    disableDraft,
    disableValidate,
    disabledCancelBtn,
    disableEditBtn,

    /** Button variants & styles */
    cancelButtonVariant,
    cancelButtonOutlined,
    purpleSaveButton,
    saveButtonVariant,

    /** Tooltip */
    showTooltip,
    tooltipText,

    /** Labels */
    labels,

    /** Checkbox option */
    checkboxState,

    /** Event handlers */
    onCancel,
    onDelete,
    onActivate,
    onDeactivate,
    onDraft,
    onPublish,
    onSave,
    onEdit,

    /** Rest */
    ...rest
  } = props;

  const classes = useStyles(props);

  const appAction = useAppAction();
  const intl = useIntl();
  const scrollPosition = useWindowScroll();
  const [buttonClicked, setButtonClicked] = useState(false);

  const handleOnDraft = useCallback(
    async event => {
      if (buttonClicked) {
        return;
      }
      setButtonClicked(true);
      await onDraft(event);
      setTimeout(() => {
        setButtonClicked(false);
      }, 1000);
    },
    [buttonClicked, onDraft]
  );

  React.useEffect(() => {
    if ((!disabled && state !== "loading") || forceDocked) {
      appAction.setDocked(false);
    }
  }, [disabled]);
  React.useEffect(() => () => appAction.setDocked(!forceDocked), []);

  const scrolledToBottom =
    scrollPosition.y + window.innerHeight >= document.body.scrollHeight;

  return appAction.anchor ? (
    <Portal container={appAction.anchor.current}>
      <div className={classes.root} {...rest}>
        <Container>
          <Card
            className={classes.paper}
            elevation={!(appAction.docked || scrolledToBottom) ? 16 : 0}
          >
            <CardContent className={classes.content}>
              {!!onEdit && (
                <Button
                  id={
                    labels?.edit
                      ? `${labels?.edit.toLowerCase()}Btn`
                      : `editBtn`
                  }
                  variant="text"
                  color="primary"
                  onClick={onEdit}
                  data-test="button-bar-edit"
                  className={classes.editButton}
                  disabled={disableEditBtn}
                >
                  {labels?.edit || intl.formatMessage(buttonMessages.edit)}
                </Button>
              )}

              {!!checkboxState && (
                <FormControlLabel
                  className={checkboxState.styles}
                  disabled={checkboxState.disabled}
                  label={checkboxState.label}
                  control={
                    <Checkbox
                      checked={checkboxState.checked}
                      onChange={checkboxState.onChange}
                      color="primary"
                    />
                  }
                />
              )}

              {!!onDelete && status ? (
                <Button
                  id={
                    labels?.delete
                      ? `${labels?.delete.toLowerCase()}Btn`
                      : `deleteBtn`
                  }
                  variant="contained"
                  color="primary"
                  onClick={onDelete}
                  // product status condition
                  disabled={
                    ((status && status === "DRAFT") ||
                      (status && status === "UNLISTED")) &&
                    state &&
                    state !== "error"
                      ? false
                      : true
                  }
                  className={classes.deleteButton}
                  data-test="button-bar-delete"
                >
                  {labels?.delete || intl.formatMessage(buttonMessages.delete)}
                </Button>
              ) : !!onDelete ? (
                <Button
                  id={
                    labels?.delete
                      ? `${labels?.delete.toLowerCase()}Btn`
                      : `deleteBtn`
                  }
                  variant="contained"
                  color="primary"
                  onClick={onDelete}
                  className={classes.deleteButton}
                  data-test="button-bar-delete"
                  disabled={disableDelete}
                >
                  {labels?.delete || intl.formatMessage(buttonMessages.delete)}
                </Button>
              ) : (
                <></>
              )}
              {!!onDeactivate && hideDeactivate ? (
                <>
                  <Button
                    id={
                      labels?.deactivate
                        ? `${labels?.deactivate.toLowerCase()}Btn`
                        : `deactivateBtn`
                    }
                    variant="text"
                    color="secondary"
                    onClick={onDeactivate}
                    data-test="button-bar-deactivate"
                    className={classes.deactivateButton}
                  >
                    {labels?.deactivate ||
                      intl.formatMessage(buttonMessages.deactivate)}
                  </Button>
                </>
              ) : (
                <></>
              )}
              {onActivate && (
                <Button
                  id={
                    labels?.activate
                      ? `${labels.activate.toLowerCase()}Btn`
                      : `activate`
                  }
                  variant="text"
                  color="secondary"
                  onClick={onActivate}
                  data-test="button-bar-activate"
                  className={classes.activateButton}
                >
                  <div className={classes.activateText}>
                    {labels?.activate ||
                      intl.formatMessage(buttonMessages.activate)}
                  </div>
                </Button>
              )}

              <div className={classes.spacer} />
              {!!onDraft ? (
                <>
                  <ConfirmButton
                    id={
                      labels?.draft
                        ? `${labels?.draft.toLowerCase()}Btn`
                        : `draftBtn`
                    }
                    onClick={event => handleOnDraft(event)}
                    data-test="button-bar-draft"
                    className={classes.draftButton}
                    disabled={buttonClicked}
                    transitionState={state}
                    onTransitionToDefault={() => appAction.setDocked(true)}
                  >
                    <div className={classes.draftText}>
                      {labels?.draft ||
                        intl.formatMessage(buttonMessages.saveDraft)}
                    </div>
                  </ConfirmButton>
                </>
              ) : (
                <></>
              )}

              <Button
                id="onCancelBtn"
                className={`${
                  cancelButtonVariant
                    ? classes.cancelButtonSecondary
                    : classes.cancelButton
                } ${cancelButtonOutlined ? classes.cancelButtonOutlined : ""}`}
                variant={cancelButtonOutlined ? "outlined" : "text"}
                onClick={onCancel}
                data-test="button-bar-cancel"
                disabled={disabledCancelBtn}
              >
                {labels?.cancel ||
                  (showCancelButton
                    ? intl.formatMessage(buttonMessages.cancel)
                    : intl.formatMessage(buttonMessages.back))}
              </Button>

              {!!onPublish ? (
                <>
                  <ConfirmButton
                    disabled={disablePublishBtn}
                    id={
                      labels?.publish
                        ? `${labels?.publish.toLowerCase()}Btn`
                        : `publishBtn`
                    }
                    onClick={onPublish}
                    className={classes.publishButton}
                    data-test="button-bar-publish"
                    transitionState={state}
                    onTransitionToDefault={() => appAction.setDocked(true)}
                  >
                    <div className={classes.publishText}>
                      {labels?.publish ||
                        intl.formatMessage(buttonMessages.publish)}
                    </div>
                  </ConfirmButton>
                </>
              ) : (
                <></>
              )}

              {hideSave ? null : (
                <>
                  {saveButtonVariant ? (
                    <ConfirmButton
                      disabled={disableAlternateButton}
                      id={
                        labels?.save
                          ? `${labels?.save.toLowerCase()}Btn`
                          : `saveBtn`
                      }
                      onClick={onSave}
                      className={classes.alternateSaveButton}
                      data-test="button-bar-confirm"
                      transitionState={state}
                      onTransitionToDefault={() => appAction.setDocked(true)}
                    >
                      <div className={classes.alternateSaveButtonLabel}>
                        {labels?.save ||
                          intl.formatMessage(buttonMessages.save)}
                      </div>
                    </ConfirmButton>
                  ) : (
                    <ConfirmButton
                      disabled={disabled}
                      id={
                        labels?.save
                          ? `${labels?.save.toLowerCase()}Btn`
                          : `saveBtn`
                      }
                      onClick={onSave}
                      className={classNames({
                        [classes.purpleSaveButton]:
                          isNationwideRTS(
                            order?.shippingMethod?.deliveryType,
                            order?.smopStatus
                          ) || purpleSaveButton,
                        [classes.saveButton]:
                          !isNationwideRTS || !purpleSaveButton
                      })}
                      transitionState={state}
                      data-test="button-bar-confirm"
                      onTransitionToDefault={() => appAction.setDocked(true)}
                    >
                      {labels?.save || intl.formatMessage(buttonMessages.save)}
                    </ConfirmButton>
                  )}
                  <StoreOrderInStoreTooltip
                    visible={showTooltip}
                    text={tooltipText}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Container>
      </div>
    </Portal>
  ) : null;
};
SaveButtonBar.displayName = "SaveButtonBar";
export default SaveButtonBar;
